import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Mail, Phone, MapPinIcon, X, Star, MessageCircle, ChevronDown, ChevronUp, ArrowUp, Scissors, Utensils, Wrench, Stethoscope, Building } from 'lucide-react';
import { Button } from './components/ui/button';
import './App.css';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: 'easeOut' }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function App() {
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [showAGB, setShowAGB] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(true);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });
  const [openFaq, setOpenFaq] = useState(null);

  // Check if cookies were already accepted
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      setShowCookieBanner(false);
      try {
        const savedPrefs = JSON.parse(cookieConsent);
        if (typeof savedPrefs === 'object') {
          setCookiePreferences(savedPrefs);
        }
      } catch (e) {
        // Old format, ignore
      }
    }
  }, []);

  // Show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setCookiePreferences(allAccepted);
    setShowCookieBanner(false);
    setShowCookieSettings(false);
  };

  const acceptNecessaryCookies = () => {
    const necessaryOnly = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setCookiePreferences(necessaryOnly);
    setShowCookieBanner(false);
    setShowCookieSettings(false);
  };

  const saveSelectedCookies = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
    setShowCookieBanner(false);
    setShowCookieSettings(false);
  };

  const toggleCookiePreference = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const packages = [
    {
      name: 'Basic',
      price: '349€',
      priceNote: 'einmalig',
      monthlyPrice: '+ 29€/Monat',
      tagline: 'Professionelle Online-Präsenz',
      features: [
        '1-seitige responsive Website',
        'Mobile-optimiertes Design',
        'Kontaktformular mit E-Mail-Benachrichtigung',
        'Klick-to-Call & WhatsApp-Button',
        'Google Maps Integration',
        'FAQ-Bereich',
        'Stellenanzeigen-Bereich',
        'SEO-Grundoptimierung (Meta-Tags, Sitemap)',
        'Cookie-Banner (DSGVO-konform)',
        'Impressum & Datenschutz',
        '1 Korrekturschleife'
      ],
      cta: 'Jetzt professionell starten'
    },
    {
      name: 'Professional',
      price: '549€',
      priceNote: 'einmalig',
      monthlyPrice: '+ 49€/Monat',
      tagline: 'Lokales Lead-System',
      features: [
        'Alles aus Basic +',
        'Bis zu 5 Unterseiten',
        'Preislisten, Speisekarten & Leistungsübersichten',
        'Online-Terminbuchung mit Kalender',
        'Automatische E-Mail-Bestätigungen',
        'Kundenbewertungen & Referenzen-Bereich',
        'Bildergalerie für Ihre Arbeiten',
        'Karriereseite mit Bewerbungsformular',
        'Google Analytics Integration',
        '2 lokale SEO-Landingpages',
        '2 Korrekturschleifen'
      ],
      popular: true,
      cta: 'Mehr Anfragen sichern'
    },
    {
      name: 'Project Pro',
      price: 'ab 949€',
      priceNote: 'einmalig',
      monthlyPrice: '+ 79€/Monat',
      tagline: 'Regionale Marktführerschaft',
      features: [
        'Alles aus Professional +',
        'Bis zu 10 Unterseiten',
        'Blog-/News-Bereich',
        '5–10 lokale SEO-Landingpages',
        'Mehrstufige Anfrageformulare',
        'Mitarbeiter-/Team-Seite',
        'Karriereportal mit mehreren Stellen',
        'Download-Bereich (PDFs, Kataloge)',
        'Erweiterte Analytics & Reporting',
        '3 Monate Support nach Launch',
        '3 Korrekturschleifen'
      ],
      cta: 'Individuelle Lösung anfragen'
    }
  ];

  const industries = [
    { icon: <Scissors size={40} />, name: 'Friseure & Beauty', description: 'Online-Buchungen und Galerie' },
    { icon: <Utensils size={40} />, name: 'Restaurants & Cafés', description: 'Speisekarten und Reservierungen' },
    { icon: <Wrench size={40} />, name: 'Handwerker', description: 'Projekte und Kontaktanfragen' },
    { icon: <Stethoscope size={40} />, name: 'Ärzte & Praxen', description: 'Terminbuchung und Info' },
    { icon: <Building size={40} />, name: 'Lokale Shops', description: 'Produkte und Standort' }
  ];

  const processSteps = [
    { step: '01', title: 'Erstgespräch', description: 'Kostenlose 30-minütige Beratung über Ihre Anforderungen' },
    { step: '02', title: 'Entwurf', description: 'Kostenloser Mockup-Entwurf für Ihr Projekt' },
    { step: '03', title: 'Entwicklung', description: 'Professionelle Umsetzung Ihrer Website' },
    { step: '04', title: 'Launch', description: 'Go-Live und Übergabe mit Schulung' }
  ];

  const testimonials = [
    { name: 'Maria S.', business: 'Friseursalon', rating: 5, text: 'Super Service! Meine neue Website hat mir schon viele neue Kunden gebracht.' },
    { name: 'Thomas K.', business: 'Elektrikerbetrieb', rating: 5, text: 'Schnell, professionell und bezahlbar. Genau was ich gesucht habe.' },
    { name: 'Lisa M.', business: 'Café Besitzerin', rating: 5, text: 'Endlich kann man unsere Speisekarte online sehen. Die Umsetzung war perfekt!' }
  ];

  const faqs = [
    { question: 'Wie lange dauert die Erstellung einer Website?', answer: 'Je nach Umfang zwischen 1-4 Wochen. Eine einfache Landing Page kann bereits in einer Woche fertig sein.' },
    { question: 'Was kostet eine Website bei LOCAL 2 LOCAL?', answer: 'Unsere Pakete beginnen bei 400€ für eine Basic Landing Page. Für individuelle Projekte erstellen wir gerne ein maßgeschneidertes Angebot.' },
    { question: 'Ist der erste Entwurf wirklich kostenlos?', answer: 'Ja! Wir erstellen einen kostenlosen Mockup-Entwurf, damit Sie sehen können, wie Ihre Website aussehen wird, bevor Sie sich entscheiden.' },
    { question: 'Brauche ich technisches Wissen?', answer: 'Nein, wir kümmern uns um alles Technische. Sie müssen nur Ihre Inhalte (Texte, Bilder) bereitstellen.' },
    { question: 'Was passiert nach dem Launch?', answer: 'Je nach Paket erhalten Sie 3-12 Monate Support. Danach bieten wir günstige Wartungspakete an.' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="App">
      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-white border-b border-black z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_35d6521c-49b6-4e21-ab97-a2d89f94dc46/artifacts/y6wh4v35_Beige%20Black%20Modern%20Creative%20Studio%20Designer%20Typography%20Logo.svg" 
              alt="LOCAL 2 LOCAL Logo" 
              className="h-40 md:h-48 w-auto"
            />
          </div>
          <div className="hidden lg:flex gap-6">
            <button onClick={() => scrollToSection('branchen')} className="text-sm uppercase tracking-widest hover:text-gray-600 transition-colors" data-testid="nav-branchen">Branchen</button>
            <button onClick={() => scrollToSection('pricing')} className="text-sm uppercase tracking-widest hover:text-gray-600 transition-colors" data-testid="nav-pricing">Leistungen</button>
            <button onClick={() => scrollToSection('prozess')} className="text-sm uppercase tracking-widest hover:text-gray-600 transition-colors" data-testid="nav-prozess">Prozess</button>
            <button onClick={() => scrollToSection('bewertungen')} className="text-sm uppercase tracking-widest hover:text-gray-600 transition-colors" data-testid="nav-bewertungen">Bewertungen</button>
            <button onClick={() => scrollToSection('faq')} className="text-sm uppercase tracking-widest hover:text-gray-600 transition-colors" data-testid="nav-faq">FAQ</button>
            <button onClick={() => scrollToSection('booking')} className="text-sm uppercase tracking-widest hover:text-gray-600 transition-colors" data-testid="nav-booking">Kontakt</button>
          </div>
          <Button onClick={() => scrollToSection('booking')} className="bg-black text-white hover:bg-gray-800 rounded-none uppercase tracking-widest text-xs px-6 py-3" data-testid="nav-cta-button">
            Jetzt Starten
          </Button>
        </div>
      </nav>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/4917622558552"
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed right-4 md:right-6 bg-green-500 text-white p-3 md:p-4 rounded-full shadow-lg hover:bg-green-600 transition-all z-40 hover:scale-110 ${showCookieBanner ? 'bottom-44 md:bottom-32' : 'bottom-20 md:bottom-24'}`}
        data-testid="whatsapp-button"
      >
        <MessageCircle size={24} className="md:w-7 md:h-7" />
      </a>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className={`fixed left-4 md:left-6 bg-white text-black border-2 border-black p-3 md:p-4 rounded-full shadow-lg hover:bg-black hover:text-white transition-all z-40 ${showCookieBanner ? 'bottom-44 md:bottom-32' : 'bottom-20 md:bottom-24'}`}
          data-testid="scroll-top-button"
          aria-label="Nach oben scrollen"
        >
          <ArrowUp size={24} className="md:w-7 md:h-7" />
        </motion.button>
      )}

      {/* Hero Section */}
      <section id="hero" className="min-h-screen bg-black text-white flex items-center justify-center grain hero-pattern relative pt-20">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_35d6521c-49b6-4e21-ab97-a2d89f94dc46/artifacts/4rm53xya_Beige%20Black%20Modern%20Creative%20Studio%20Designer%20Typography%20Logo%20%281%29.svg" 
            alt="LOCAL 2 LOCAL Wasserzeichen" 
            className="w-[800px] h-auto"
          />
        </div>
        
        <motion.div 
          className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.h1 
            className="text-6xl md:text-8xl font-bold leading-none mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            data-testid="hero-title"
          >
            WIR BAUEN IHRE<br />DIGITALE PRÄSENZ
          </motion.h1>
          <motion.p 
            className="text-lg md:text-xl text-gray-300 mb-4 max-w-2xl mx-auto font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            data-testid="hero-subtitle"
          >
            Professionelle Websites für lokale Unternehmen. Von der Idee bis zur Online-Präsenz.
          </motion.p>
          <motion.p 
            className="text-md text-green-400 mb-12 font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            data-testid="free-mockup-badge"
          >
            ✓ Kostenloser Entwurf für Ihr Projekt
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button 
              onClick={() => scrollToSection('booking')} 
              className="bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-widest text-sm px-8 py-6 text-base"
              data-testid="hero-cta-button"
            >
              Kostenlos Beraten Lassen
            </Button>
            <a href="tel:+4917622558552">
              <Button 
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-black rounded-none uppercase tracking-widest text-sm px-8 py-6 text-base"
                data-testid="hero-call-button"
              >
                <Phone size={18} className="mr-2" />
                Jetzt Anrufen
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Branchen Section */}
      <section id="branchen" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 className="text-4xl md:text-6xl font-bold mb-4" variants={fadeInUp} data-testid="branchen-title">
              Ideal für lokale Unternehmen
            </motion.h2>
            <motion.p className="text-gray-600 mb-16 text-lg" variants={fadeInUp}>
              Wir verstehen die Bedürfnisse verschiedener Branchen
            </motion.p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  className="bg-white border border-gray-200 p-6 text-center hover-lift"
                  variants={fadeInUp}
                  data-testid={`industry-card-${index}`}
                >
                  <div className="mb-4 flex justify-center text-black">{industry.icon}</div>
                  <h3 className="font-bold mb-2">{industry.name}</h3>
                  <p className="text-gray-500 text-sm">{industry.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 className="text-4xl md:text-6xl font-bold mb-4" variants={fadeInUp} data-testid="pricing-title">
              Unsere Leistungspakete
            </motion.h2>
            <motion.p className="text-gray-400 mb-16 text-lg" variants={fadeInUp}>
              Klare Leistungen. Messbare Ergebnisse. Faire Preise.
            </motion.p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages.map((pkg, index) => (
                <motion.div
                  key={index}
                  className={`border p-8 relative ${pkg.popular ? 'border-white bg-white text-black' : 'border-gray-700'}`}
                  variants={fadeInUp}
                  data-testid={`pricing-card-${index}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-black text-white px-4 py-1 text-sm uppercase tracking-widest">
                      Beliebteste Wahl
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-1">{pkg.name}</h3>
                  <p className={`text-sm mb-3 ${pkg.popular ? 'text-gray-600' : 'text-gray-400'}`}>{pkg.tagline}</p>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">{pkg.price}</span>
                    <span className={`text-sm ml-2 ${pkg.popular ? 'text-gray-600' : 'text-gray-400'}`}>{pkg.priceNote}</span>
                  </div>
                  <p className={`text-lg font-semibold mb-6 ${pkg.popular ? 'text-gray-700' : 'text-gray-300'}`}>{pkg.monthlyPrice}</p>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check size={16} className="mt-1 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => scrollToSection('booking')}
                    className={`w-full rounded-none uppercase tracking-widest text-xs py-4 ${pkg.popular ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black hover:bg-gray-200'}`}
                    data-testid={`pricing-cta-${index}`}
                  >
                    {pkg.cta}
                  </Button>
                </motion.div>
              ))}
            </div>

            <motion.div className="mt-12 text-center" variants={fadeInUp}>
              <div className="bg-gray-900 border border-gray-700 p-6 mb-8 max-w-2xl mx-auto">
                <h4 className="font-bold mb-3 text-lg">Monatliche Wartung beinhaltet:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm text-gray-300">
                  <div className="flex items-center gap-2"><Check size={14} /> Schnelles Hosting</div>
                  <div className="flex items-center gap-2"><Check size={14} /> SSL-Zertifikat</div>
                  <div className="flex items-center gap-2"><Check size={14} /> Wöchentliche Backups</div>
                  <div className="flex items-center gap-2"><Check size={14} /> Sicherheitsupdates</div>
                  <div className="flex items-center gap-2"><Check size={14} /> Kleine Textänderungen</div>
                  <div className="flex items-center gap-2"><Check size={14} /> E-Mail-Support</div>
                </div>
              </div>
              <p className="text-gray-400 mb-4">Nicht sicher welches Paket passt?</p>
              <Button 
                onClick={() => scrollToSection('booking')}
                className="bg-transparent border border-white text-white hover:bg-white hover:text-black rounded-none uppercase tracking-widest"
                data-testid="free-consultation-cta"
              >
                30 Min. Gratis Beratung
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline Section */}
      <section id="prozess" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 className="text-4xl md:text-6xl font-bold mb-4" variants={fadeInUp} data-testid="prozess-title">
              Unser Prozess
            </motion.h2>
            <motion.p className="text-gray-600 mb-16 text-lg" variants={fadeInUp}>
              In 4 einfachen Schritten zu Ihrer Website
            </motion.p>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {processSteps.map((item, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  variants={fadeInUp}
                  data-testid={`process-step-${index}`}
                >
                  <div className="text-7xl font-bold text-gray-100 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-0.5 bg-gray-200" style={{ left: '50%' }}></div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="bewertungen" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 className="text-4xl md:text-6xl font-bold mb-4" variants={fadeInUp} data-testid="bewertungen-title">
              Das sagen unsere Kunden
            </motion.h2>
            <motion.p className="text-gray-600 mb-16 text-lg" variants={fadeInUp}>
              Zufriedene Kunden sind unser größter Erfolg
            </motion.p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  className="bg-white border border-gray-200 p-8"
                  variants={fadeInUp}
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.business}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 className="text-4xl md:text-6xl font-bold mb-4 text-center" variants={fadeInUp} data-testid="faq-title">
              Häufige Fragen
            </motion.h2>
            <motion.p className="text-gray-600 mb-16 text-lg text-center" variants={fadeInUp}>
              Hier finden Sie Antworten auf die häufigsten Fragen
            </motion.p>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className="border border-gray-200"
                  variants={fadeInUp}
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                    data-testid={`faq-toggle-${index}`}
                  >
                    <span className="font-semibold pr-4">{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 text-gray-600" data-testid={`faq-answer-${index}`}>
                      {faq.answer}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Booking Section - meetergo Integration */}
      <section id="booking" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 className="text-4xl md:text-6xl font-bold mb-4" variants={fadeInUp} data-testid="booking-title">
              Termin Buchen
            </motion.h2>
            <motion.p className="text-gray-600 mb-8 text-lg" variants={fadeInUp}>
              Vereinbaren Sie ein kostenloses 30-minütiges Erstgespräch
            </motion.p>
            
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
              <div className="bg-white border border-gray-200 p-4 md:p-8" data-testid="meetergo-container">
                <iframe
                  src="https://cal.meetergo.com/infolocal-2local?lang=de"
                  width="100%"
                  height="700"
                  frameBorder="0"
                  title="Kostenloses Erstgespräch buchen - meetergo"
                  data-testid="meetergo-iframe"
                  style={{ border: 'none', minHeight: '700px' }}
                  allow="camera; microphone; fullscreen; payment"
                />
              </div>
              <p className="text-center text-gray-500 text-sm mt-4">
                Powered by meetergo – Sichere Online-Terminbuchung
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_35d6521c-49b6-4e21-ab97-a2d89f94dc46/artifacts/4rm53xya_Beige%20Black%20Modern%20Creative%20Studio%20Designer%20Typography%20Logo%20%281%29.svg" 
                alt="LOCAL 2 LOCAL" 
                className="h-16 mb-4 w-auto"
              />
              <p className="text-gray-400 text-sm">
                Professionelle Websites für lokale Unternehmen. Von der Idee bis zur Online-Präsenz.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 uppercase tracking-widest text-sm">Kontakt</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-center gap-2">
                  <Phone size={14} />
                  <a href="tel:+491234567890" className="hover:text-white transition-colors">+49 123 456 7890</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={14} />
                  <a href="mailto:info@local2local.de" className="hover:text-white transition-colors">info@local2local.de</a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPinIcon size={14} />
                  <span>Deutschland</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 uppercase tracking-widest text-sm">Leistungen</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pakete</button></li>
                <li><button onClick={() => scrollToSection('prozess')} className="hover:text-white transition-colors">Prozess</button></li>
                <li><button onClick={() => scrollToSection('booking')} className="hover:text-white transition-colors">Beratung</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 uppercase tracking-widest text-sm">Rechtliches</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><button onClick={() => setShowImpressum(true)} className="hover:text-white transition-colors" data-testid="footer-impressum">Impressum</button></li>
                <li><button onClick={() => setShowDatenschutz(true)} className="hover:text-white transition-colors" data-testid="footer-datenschutz">Datenschutz</button></li>
                <li><button onClick={() => setShowAGB(true)} className="hover:text-white transition-colors" data-testid="footer-agb">AGB</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">© 2026 LOCAL 2 LOCAL. Alle Rechte vorbehalten.</p>
            <a href="tel:+491234567890">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-widest text-xs px-6 py-3 flex items-center gap-2" data-testid="footer-call-button">
                <Phone size={16} />
                Jetzt Anrufen
              </Button>
            </a>
          </div>
        </div>
      </footer>

      {/* Impressum Modal */}
      {showImpressum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="impressum-modal">
          <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 relative">
            <button onClick={() => setShowImpressum(false)} className="absolute top-4 right-4" data-testid="close-impressum">
              <X size={24} />
            </button>
            <h2 className="text-3xl font-bold mb-6">Impressum</h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-bold">Angaben gemäß § 5 TMG</h3>
                <p>LOCAL 2 LOCAL<br />Musterstraße 123<br />12345 Musterstadt<br />Deutschland</p>
              </div>
              <div>
                <h3 className="font-bold">Kontakt</h3>
                <p>Telefon: +49 123 456 7890<br />E-Mail: info@local2local.de</p>
              </div>
              <div>
                <h3 className="font-bold">Umsatzsteuer-ID</h3>
                <p>Umsatzsteuer-Identifikationsnummer gemäß §27 a Umsatzsteuergesetz: DE123456789</p>
              </div>
              <div>
                <h3 className="font-bold">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h3>
                <p>Paul Szanto<br />Musterstraße 123<br />12345 Musterstadt</p>
              </div>
              <div>
                <h3 className="font-bold">Haftungsausschluss</h3>
                <p className="text-sm">Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Datenschutz Modal */}
      {showDatenschutz && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="datenschutz-modal">
          <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 relative">
            <button onClick={() => setShowDatenschutz(false)} className="absolute top-4 right-4" data-testid="close-datenschutz">
              <X size={24} />
            </button>
            <h2 className="text-3xl font-bold mb-6">Datenschutzerklärung</h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-bold">1. Datenschutz auf einen Blick</h3>
                <p className="text-sm">Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen.</p>
              </div>
              <div>
                <h3 className="font-bold">2. Allgemeine Hinweise und Pflichtinformationen</h3>
                <p className="text-sm">Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.</p>
              </div>
              <div>
                <h3 className="font-bold">3. Datenerfassung auf unserer Website</h3>
                <p className="text-sm">Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.</p>
              </div>
              <div>
                <h3 className="font-bold">4. Ihre Rechte</h3>
                <p className="text-sm">Sie haben jederzeit das Recht auf Auskunft über die gespeicherten personenbezogenen Daten, deren Herkunft, Empfänger und den Zweck der Datenverarbeitung sowie ein Recht auf Berichtigung, Sperrung oder Löschung dieser Daten.</p>
              </div>
              <div>
                <h3 className="font-bold">5. Kontaktformular</h3>
                <p className="text-sm">Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AGB Modal */}
      {showAGB && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="agb-modal">
          <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 relative">
            <button onClick={() => setShowAGB(false)} className="absolute top-4 right-4" data-testid="close-agb">
              <X size={24} />
            </button>
            <h2 className="text-3xl font-bold mb-6">Allgemeine Geschäftsbedingungen</h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-bold">§ 1 Geltungsbereich</h3>
                <p className="text-sm">Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen LOCAL 2 LOCAL (nachfolgend "Anbieter") und dem Kunden über die Erstellung von Websites und digitalen Dienstleistungen.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 2 Vertragsgegenstand</h3>
                <p className="text-sm">Der Anbieter erbringt Dienstleistungen im Bereich Webdesign, Webentwicklung und digitales Marketing. Der genaue Leistungsumfang ergibt sich aus dem jeweiligen Angebot bzw. der Auftragsbestätigung.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 3 Vertragsschluss</h3>
                <p className="text-sm">Der Vertrag kommt durch die schriftliche Auftragsbestätigung des Anbieters oder durch Beginn der Leistungserbringung zustande. Angebote des Anbieters sind freibleibend.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 4 Preise und Zahlungsbedingungen</h3>
                <p className="text-sm">Alle Preise verstehen sich in Euro und zzgl. der gesetzlichen Mehrwertsteuer. Die Zahlung erfolgt wie im Angebot vereinbart. Bei Projekten über 500€ wird eine Anzahlung von 50% bei Auftragserteilung fällig.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 5 Mitwirkungspflichten des Kunden</h3>
                <p className="text-sm">Der Kunde stellt alle für die Durchführung des Projekts erforderlichen Unterlagen, Informationen und Zugänge rechtzeitig zur Verfügung. Verzögerungen durch den Kunden berechtigen den Anbieter zur Anpassung von Terminen.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 6 Abnahme</h3>
                <p className="text-sm">Der Kunde ist verpflichtet, die erbrachten Leistungen innerhalb von 14 Tagen nach Fertigstellung abzunehmen. Erfolgt keine Rückmeldung, gilt die Leistung als abgenommen.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 7 Nutzungsrechte</h3>
                <p className="text-sm">Mit vollständiger Bezahlung gehen die Nutzungsrechte an den erstellten Inhalten auf den Kunden über. Der Anbieter behält das Recht, die Arbeiten zu Referenzzwecken zu verwenden.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 8 Haftung</h3>
                <p className="text-sm">Der Anbieter haftet nur für Schäden, die auf vorsätzlichem oder grob fahrlässigem Verhalten beruhen. Die Haftung ist auf die Höhe des Auftragswertes begrenzt.</p>
              </div>
              <div>
                <h3 className="font-bold">§ 9 Schlussbestimmungen</h3>
                <p className="text-sm">Es gilt das Recht der Bundesrepublik Deutschland. Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Banner */}
      {showCookieBanner && !showCookieSettings && (
        <div className="fixed bottom-0 left-0 right-0 bg-white text-black p-4 md:p-6 z-50 shadow-2xl border-t border-gray-200" data-testid="cookie-banner">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="font-bold text-base md:text-lg mb-2">Cookie-Einstellungen</h3>
                <p className="text-xs md:text-sm text-gray-600">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Sie können selbst entscheiden, welche Cookies Sie zulassen möchten. 
                  Weitere Informationen finden Sie in unserer{' '}
                  <button 
                    onClick={() => setShowDatenschutz(true)} 
                    className="underline hover:text-black font-medium"
                  >
                    Datenschutzerklärung
                  </button>.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={() => setShowCookieSettings(true)}
                  className="bg-transparent border border-gray-300 text-black hover:bg-gray-100 rounded-none px-4 py-2 text-xs sm:text-sm w-full sm:w-auto"
                  data-testid="cookie-settings"
                >
                  Einstellungen
                </Button>
                <Button
                  onClick={acceptNecessaryCookies}
                  className="bg-transparent border border-black text-black hover:bg-gray-100 rounded-none px-4 py-2 text-xs sm:text-sm w-full sm:w-auto"
                  data-testid="cookie-necessary-only"
                >
                  Nur Notwendige
                </Button>
                <Button
                  onClick={acceptAllCookies}
                  className="bg-black text-white hover:bg-gray-800 rounded-none px-4 py-2 text-xs sm:text-sm w-full sm:w-auto"
                  data-testid="cookie-accept-all"
                >
                  Alle Akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showCookieSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid="cookie-settings-modal">
          <div className="bg-white w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative rounded-t-2xl sm:rounded-none">
            <button onClick={() => setShowCookieSettings(false)} className="absolute top-4 right-4">
              <X size={24} />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Cookie-Einstellungen</h2>
            <p className="text-gray-600 mb-6 text-xs sm:text-sm">
              Hier können Sie Ihre Cookie-Präferenzen verwalten. Notwendige Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
            </p>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {/* Notwendige Cookies */}
              <div className="border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold">Notwendige Cookies</h3>
                  <div className="bg-gray-200 text-gray-600 px-3 py-1 text-xs rounded">Immer aktiv</div>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies sind für die Grundfunktionen der Website erforderlich (z.B. Speicherung Ihrer Cookie-Einstellungen, Sicherheitsfunktionen).
                </p>
              </div>

              {/* Funktionale Cookies */}
              <div className="border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold">Funktionale Cookies</h3>
                  <button
                    onClick={() => toggleCookiePreference('functional')}
                    className={`w-12 h-6 rounded-full transition-colors ${cookiePreferences.functional ? 'bg-black' : 'bg-gray-300'}`}
                    data-testid="toggle-functional"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${cookiePreferences.functional ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung (z.B. Spracheinstellungen, Formular-Vorausfüllungen).
                </p>
              </div>

              {/* Analyse Cookies */}
              <div className="border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold">Analyse Cookies</h3>
                  <button
                    onClick={() => toggleCookiePreference('analytics')}
                    className={`w-12 h-6 rounded-full transition-colors ${cookiePreferences.analytics ? 'bg-black' : 'bg-gray-300'}`}
                    data-testid="toggle-analytics"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${cookiePreferences.analytics ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren (z.B. Google Analytics, Besucherzahlen).
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold">Marketing Cookies</h3>
                  <button
                    onClick={() => toggleCookiePreference('marketing')}
                    className={`w-12 h-6 rounded-full transition-colors ${cookiePreferences.marketing ? 'bg-black' : 'bg-gray-300'}`}
                    data-testid="toggle-marketing"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${cookiePreferences.marketing ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies werden verwendet, um Ihnen relevante Werbung anzuzeigen (z.B. personalisierte Anzeigen, Retargeting).
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end border-t pt-4 sm:pt-6">
              <Button
                onClick={acceptNecessaryCookies}
                className="bg-transparent border border-gray-300 text-black hover:bg-gray-100 rounded-none px-4 py-2 text-xs sm:text-sm w-full sm:w-auto order-3 sm:order-1"
              >
                Nur Notwendige
              </Button>
              <Button
                onClick={saveSelectedCookies}
                className="bg-transparent border border-black text-black hover:bg-gray-100 rounded-none px-4 py-2 text-xs sm:text-sm w-full sm:w-auto order-2"
              >
                Auswahl Speichern
              </Button>
              <Button
                onClick={acceptAllCookies}
                className="bg-black text-white hover:bg-gray-800 rounded-none px-4 py-2 text-xs sm:text-sm w-full sm:w-auto order-1 sm:order-3"
              >
                Alle Akzeptieren
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
